import {
  Dispatch, FormEvent, Fragment, SetStateAction, useCallback, useEffect, useState,
} from 'react';

import { Menu, Transition } from '@headlessui/react';
import { useTranslation } from 'next-i18next';
import { bjToast, Button, Modal } from '@components/common';

import {
  ApiGetSpokenLanguageLevels,
  ApiSpokenLanguages, fetchAbort,
  fetcher,
  isSpokenLanguageLevelsOk,
  isSpokenLanguagesOk,
} from '@utils/data-fetching';
import { IGenericApiStrings } from 'src/types/generic-api-strings';
import { IDisplayedLanguage } from 'src/types/displayed-languages';
import {
  ArrowLeftIcon, XMarkIcon, CheckIcon, PlusIcon, MagnifyingGlassIcon, ChevronDownIcon,
} from '@heroicons/react/24/solid';

interface IProps {
  setSelectedLanguages: Dispatch<SetStateAction<IDisplayedLanguage[]>>;
  isEnglishSelected: boolean,
  locale: Locale,
}

/**
 * @description Shows a list of languages that user can select
 * @param setSelectedLanguages - used for setting selected languages to parent component
 * @param isEnglishSelected - custom case for english. appears on screen with
 * @param locale - the current app locale
 */
export const LanguagesForm = ({ setSelectedLanguages, isEnglishSelected, locale }: IProps) => {
  const { t } = useTranslation('common');

  const [languages, setLanguages] = useState<IGenericApiStrings>({} as IGenericApiStrings);
  const [languageLevels, setLanguageLevels] = useState<IGenericApiStrings>({});
  const [searchedLanguages, setSearchedLanguages] = useState<string[]>([]);
  const [displayedLanguages, setDisplayedLanguages] = useState<IDisplayedLanguage[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const initialLanguageDataSetup = useCallback((apiLanguageResponse: IGenericApiStrings) => {
    setLanguages(apiLanguageResponse);
    const userLanguage: IDisplayedLanguage = { shortName: locale as string, level: '3', isSelected: true };

    const toDisplayLanguages = [userLanguage];

    if (locale !== 'en') {
      const englishLanguage = { shortName: 'en', level: '4', isSelected: isEnglishSelected };
      toDisplayLanguages.push(englishLanguage);
    }

    setDisplayedLanguages(toDisplayLanguages);
  }, [isEnglishSelected, locale]);

  useEffect(() => {
    const { controller: controllerLanguages, timeoutId: controllerLanguagesTimeoutId } = fetchAbort();
    const { controller: controllerLanguageLevels, timeoutId: controllerLanguageLevelsTimeoutId } = fetchAbort();

    // get spoken languages
    void fetcher<ApiSpokenLanguages>('/v1/static-lists/spoken-languages', locale, {
      externalAbort: { extController: controllerLanguages, extTimeout: controllerLanguagesTimeoutId },
    })
      .then((response) => {
        if (isSpokenLanguagesOk(response)) {
          initialLanguageDataSetup(response);
        }
      });

    // get spoken languages levels
    void fetcher<ApiGetSpokenLanguageLevels>('/v1/static-lists/spoken-language-levels', locale, {
      externalAbort: { extController: controllerLanguageLevels, extTimeout: controllerLanguageLevelsTimeoutId },
    })
      .then((response) => {
        if (isSpokenLanguageLevelsOk(response)) {
          setLanguageLevels(response);
        }
      });

    return () => {
      controllerLanguages.abort();
      controllerLanguageLevels.abort();
    };
  }, [locale, initialLanguageDataSetup]);

  useEffect(() => {
    setSelectedLanguages(displayedLanguages.filter((item: IDisplayedLanguage) => item.isSelected));
  }, [setSelectedLanguages, displayedLanguages]);

  const openModal = () => {
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setSearchedLanguages([]);
  };

  const onSearchInput = (e: FormEvent<HTMLInputElement>) => {
    const inputValue = e.currentTarget.value;
    if (inputValue.length < 1) { setSearchedLanguages([]); return; }
    const resultedLanguages: string[] = [];
    Object.keys(languages).forEach((key) => {
      if (languages[key]?.toLowerCase().indexOf(inputValue.toLowerCase()) > -1) {
        resultedLanguages.push(languages[key]);
      }
    });
    setSearchedLanguages(resultedLanguages);
  };

  const addLanguageToDisplayedLanguages = (newLanguage: string) => {
    let isAlreadyAdded = false;
    let newLanguageShortName = '';
    Object.entries(languages).forEach((item) => {
      if (item[1] === newLanguage) {
        // eslint-disable-next-line prefer-destructuring
        newLanguageShortName = item[0];
      }
    });

    displayedLanguages.forEach((language) => {
      if (language.shortName === newLanguageShortName) isAlreadyAdded = true;
    });
    if (isAlreadyAdded) return;


    Object.entries(languages).forEach((languageEntry) => {
      if (languageEntry[1] === newLanguage) {
        const languageToBeAdded = { shortName: languageEntry[0], level: '4', isSelected: false };
        setDisplayedLanguages((prevState) => [...prevState, languageToBeAdded]);
      }
    });

    bjToast.success(t('onboarding.step5.language.level'));
  };

  const onDiplayedLanguageClick = (language: IDisplayedLanguage) => {
    const aux = displayedLanguages.map((displayedLanguage) => {
      if (language.shortName === displayedLanguage.shortName) {
        return {
          shortName: language.shortName,
          level: displayedLanguage.level,
          isSelected: !language.isSelected,
        };
      }
      return displayedLanguage;
    });
    setDisplayedLanguages(aux);
  };

  const addLanguageLevelToDisplayedLanguages = (
    levelIndex: number,
    language: IDisplayedLanguage,
  ) => {
    const newDisplayedLanguagesArray = displayedLanguages.map(
      (displayedLanguage) => {
        if (displayedLanguage.shortName === language.shortName) {
          return {
            shortName: language.shortName,
            level: levelIndex.toString(),
            isSelected: true,
          };
        }
        return displayedLanguage;
      },
    );

    setDisplayedLanguages(newDisplayedLanguagesArray);
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-start">
        {displayedLanguages.length > 0
          && displayedLanguages.map((language: IDisplayedLanguage) => (
            <div
              role="presentation"
              onClick={() => onDiplayedLanguageClick(language)}
              key={`${language.shortName}-button`}
              className={`${
                language.isSelected ? 'bg-primary text-white' : 'bg-surface'
              } relative
                mb-3 mr-3 flex cursor-pointer items-center rounded-full  border border-primary px-4 py-2 text-sm`}
            >
              {language.hasError && <XMarkIcon className="mr-1 size-5 text-error" /> }
              {!language.hasError && (
                language.isSelected ? (
                  <CheckIcon className="mr-1 size-5" />
                ) : (
                  <PlusIcon className="mr-1 size-5" />
                )
              )}
              <div className="min-w-fit">
                {languages[language.shortName]}
              </div>
              :
              <Menu as="div" className="inline-block text-left">
                <div>
                  <Menu.Button
                    as="button"
                    className=""
                    onClick={(event: FormEvent<HTMLButtonElement>) => event.stopPropagation()}
                  >
                    <div
                      data-dropdown-toggle="dropdown"
                      className="z-50 ml-2 flex items-center"
                    >
                      {
                        {
                          0: languageLevels[0],
                          1: languageLevels[1],
                          2: languageLevels[2],
                          3: languageLevels[3],
                          4: t('onboarding.step5.choose.level'),
                        }[language.level]
                      }
                      <ChevronDownIcon className="ml-2 mr-1 size-4 rounded-full bg-white p-0.5 text-black" />
                    </div>
                  </Menu.Button>
                </div>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items
                    className="absolute left-2  right-0 top-full z-50 mt-2 origin-top-right
                     divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5
                      focus:outline-none"
                  >
                    <div className="p-1 ">
                      {Object.values(languageLevels).map(
                        (languageLevel, index) => (
                          <Menu.Item
                            key={`${language.shortName}-button-${
                              languageLevel
                            }`}
                          >
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addLanguageLevelToDisplayedLanguages(
                                    index,
                                    language,
                                  );
                                }}
                                className={`${
                                  active
                                    ? 'bg-primary text-white'
                                    : 'text-gray-900'
                                } group flex w-full items-center rounded-md p-2 text-sm`}
                              >
                                {languageLevel}
                              </button>
                            )}
                          </Menu.Item>
                        ),
                      )}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          ))}
      </div>
      <div className="mb-5 flex items-center justify-start" />

      <div className="relative flex items-center justify-start">
        <Button
          styling="outline"
          size="sm"
          onClick={() => openModal()}
          className="mb-3 mr-3 cursor-pointer rounded-full bg-surface text-sm font-bold text-primary"
        >
          <svg
            width="16"
            height="17"
            viewBox="0 0 16 17"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-2"
          >
            <path
              d="M13.3125 7.875H9.375V3.9375C9.375 3.71875 9.15625 3.5 8.9375 3.5H8.0625C7.81641 3.5 7.625 3.71875 7.625 3.9375V7.875H3.6875C3.44141 7.875 3.25 8.09375 3.25 8.3125V9.1875C3.25 9.43359 3.44141 9.625 3.6875 9.625H7.625V13.5625C7.625 13.8086 7.81641 14 8.0625 14H8.9375C9.15625 14 9.375 13.8086 9.375 13.5625V9.625H13.3125C13.5312 9.625 13.75 9.43359 13.75 9.1875V8.3125C13.75 8.09375 13.5312 7.875 13.3125 7.875Z"
              fill="currentColor"
            />
          </svg>
          {t('onboarding.step5.add.language')}
        </Button>
      </div>

      <div className="mb-20 flex items-center justify-start" />

      <Modal centered={false} size="sm" onClose={() => closeModal()} isOpen={isOpen} className="w-full max-w-md overflow-hidden rounded-xl bg-surface p-6 text-left align-middle shadow-xl transition-all">
        <h3 className="relative text-center text-xl font-bold">
          <ArrowLeftIcon
            className="absolute left-2 top-1/2 size-7 -translate-y-1/2 rounded-full bg-ink/10 p-2 text-sm
                      font-bold"
            onClick={() => closeModal()}
          />
          {t('onboarding.step5.choose.language')}
        </h3>
        <hr className="my-4" />

        <label
          htmlFor="defaultInput"
          className="relative mt-4 flex w-full"
          aria-label={t('filters.search.language.label')}
        >
          <input
            onChange={(e) => {
              onSearchInput(e);
            }}
            type="text"
            className="bg-surface pl-10 text-sm"
            placeholder={t('onboarding.step5.example.placeholder')}
          />
          <MagnifyingGlassIcon className="absolute left-2 top-1/2 size-5 -translate-y-1/2 text-md  font-bold" />
        </label>

        <div className="rounded-sm bg-surface shadow-xl">
          {searchedLanguages.length > 0
            && searchedLanguages.map((language) => (
              <div
                className="cursor-pointer border-b-2 px-4 py-2 text-sm hover:bg-primary hover:text-white"
                role="presentation"
                key={language}
                onClick={() => {
                  addLanguageToDisplayedLanguages(language);
                  closeModal();
                }}
              >
                {language}
              </div>
            ))}
        </div>
      </Modal>
    </>
  );
};
